import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiEdit2,
  FiInfo,
  FiUser,
} from "react-icons/fi";
import PayoutBeneficiaryFlowSteps from "./PayoutBeneficiaryFlowSteps";
import { pickString, readSessionJson } from "./api/payoutMutationHelpers";

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 last:border-b-0 md:grid-cols-[210px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#505E8E]">{label}</span>
      <div className="text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-5 py-2 text-sm">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <span className="max-w-[210px] text-right font-semibold text-[#1B163B]">
        {value}
      </span>
    </div>
  );
}

const displayText = (value?: string) => value?.trim() || "-";

const initialsFromName = (name: string) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "BN";
};

const maskedAccount = (accountNumber?: string) => {
  const last4 = accountNumber?.replace(/\D/g, "").slice(-4) || "";
  return last4 ? `**** ${last4}` : "-";
};

function PayoutAddBeneficiaryReview() {
  const draft = readSessionJson<Record<string, any>>("payoutBeneficiaryDraft", {});
  const beneficiaryName = displayText(draft.beneficiaryName);
  const category = displayText(pickString(draft.category, ["label", "value"]));
  const payoutAccount = displayText(
    pickString(draft.payoutAccount, ["label", "name"]),
  );
  const payoutAccountMeta = displayText(pickString(draft.payoutAccount, ["meta"]));
  const accountType = displayText(pickString(draft.accountType, ["label", "value"]));
  const accountNumber = String(draft.accountNumber || "");
  const maskedBeneficiaryAccount = maskedAccount(accountNumber);
  const ifsc = displayText(draft.ifscCode);
  const mobile = displayText(draft.mobileNumber ? `+91 ${draft.mobileNumber}` : "");
  const email = displayText(draft.email);
  const branchName = displayText(draft.branchName);
  const remarks = displayText(draft.remarks);
  const verificationStatus = draft.beneficiaryNameVerified
    ? "Name verified"
    : "Pending verification";

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Review Beneficiary
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Review beneficiary details before verification.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/payouts/beneficiaries/add"
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
          >
            <FiEdit2 />
            Edit
          </Link>
          <Link
            to="/payouts/beneficiaries/add/verify"
            className="flex h-10 items-center gap-4 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
          >
            Continue
            <FiArrowRight className="text-lg" />
          </Link>
        </div>
      </div>

      <PayoutBeneficiaryFlowSteps activeStep={2} />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EDE9FE] text-lg font-semibold text-[#6D28D9]">
              {initialsFromName(beneficiaryName)}
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                {beneficiaryName}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {category !== "-" && payoutAccount !== "-"
                  ? `${category} beneficiary for ${payoutAccount}`
                  : "-"}
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
            <DetailRow
              label="Payout Bank Account"
              value={
                <span>
                  {payoutAccount}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {payoutAccountMeta}
                  </span>
                </span>
              }
            />
            <DetailRow label="Beneficiary Name" value={beneficiaryName} />
            <DetailRow
              label="Category"
              value={
                <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                  {category}
                </span>
              }
            />
            <DetailRow
              label="Contact"
              value={
                <span>
                  {mobile}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {email}
                  </span>
                </span>
              }
            />
            <DetailRow
              label="Bank Details"
              value={
                <span>
                  {accountType}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    A/C: {maskedBeneficiaryAccount} | IFSC: {ifsc}
                  </span>
                </span>
              }
            />
            <DetailRow label="Branch" value={branchName} />
            <DetailRow label="Purpose / Remarks" value={remarks} />
            <DetailRow
              label="Verification Status"
              value={
                <span
                  className={
                    "inline-flex items-center gap-2 rounded-[5px] px-2 py-1 text-xs font-semibold " +
                    (draft.beneficiaryNameVerified
                      ? "bg-[#DCFCE7] text-[#16A34A]"
                      : "bg-[#FEF3C7] text-[#F59E0B]")
                  }
                >
                  {verificationStatus}
                </span>
              }
            />
          </div>

          <div className="mt-6 flex gap-3 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <FiInfo className="mt-0.5 shrink-0 text-lg" />
            Beneficiary details will be submitted after the verification step.
          </div>

          <div className="mt-7 flex items-center justify-between">
            <Link
              to="/payouts/beneficiaries/add"
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <Link
              to="/payouts/beneficiaries/add/verify"
              className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
            >
              Continue to Verification
              <FiArrowRight />
            </Link>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Beneficiary Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <div className="mb-5 flex items-center gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EDE9FE] text-lg font-semibold text-[#6D28D9]">
              <FiUser />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[#1B163B]">
                {beneficiaryName}
              </span>
              <span className="mt-1 inline-flex rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                New Beneficiary
              </span>
            </span>
          </div>
          <SummaryRow label="Account" value={payoutAccount} />
          <SummaryRow label="Bank" value="-" />
          <SummaryRow label="Account Number" value={maskedBeneficiaryAccount} />
          <SummaryRow label="IFSC" value={ifsc} />
          <SummaryRow label="Category" value={category} />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            <FiCheckCircle className="mr-2 inline" />
            Ready for Google Authenticator verification.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutAddBeneficiaryReview;
